/**
 * /eleve/mes-moniteurs — Fiches informelles moniteur/école côté élève (Phase 31.B)
 *
 * L'élève peut créer une fiche libre pour référencer un moniteur ou une école
 * (utile s'il apprend hors code2roues). Option : envoyer un email d'invitation.
 * Aucune obligation pour le moniteur cité.
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  Plus, Users, Building2, Trash2, Mail, MailCheck, X, Loader2, CheckCircle2, AlertCircle, Info,
} from 'lucide-react';
import api from '../../api';
import { toast } from 'sonner';

export default function MesMoniteursPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.eleveMoniteursList();
      setItems(r?.items || []);
    } catch (e) {
      toast.error('Impossible de charger tes moniteurs', { description: e.message });
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    document.title = 'Mes moniteurs · Espace élève';
    load();
  }, [load]);

  const remove = async (id, name) => {
    if (!window.confirm(`Supprimer la fiche de ${name} ?`)) return;
    try {
      await api.eleveMoniteursDelete(id);
      toast.success('Fiche supprimée');
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <div className="max-w-4xl" data-testid="mes-moniteurs-page">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <div className="text-[10.5px] font-black uppercase tracking-[0.22em] text-rose-500 mb-2">Élève · Mes moniteurs</div>
          <h1 className="text-4xl sm:text-5xl font-black text-white tracking-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Mes moniteurs</h1>
          <p className="text-sm text-white/60 mt-2 max-w-2xl">
            Garde une trace personnelle de tes moniteurs / auto-écoles, même s&apos;ils ne sont pas sur code2roues.
            Tu peux leur envoyer une invitation email pour qu&apos;ils te rejoignent officiellement (facultatif).
          </p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          data-testid="btn-open-new-informal"
          className="shrink-0 inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-rose-600 hover:bg-rose-700 text-white text-[13px] font-semibold shadow-md shadow-rose-900/30 transition"
        >
          <Plus size={15} /> Ajouter un moniteur
        </button>
      </div>

      <div className="rounded-xl border border-blue-500/20 bg-blue-500/[0.04] p-3 mb-6 flex items-start gap-2 text-[12px] text-blue-100/80">
        <Info size={14} className="text-blue-300 shrink-0 mt-0.5" />
        <div>
          Si ton moniteur a un <strong>QR code code2roues</strong>, scanne-le plutôt — le rattachement se fait alors automatiquement
          et t&apos;évite de recopier ses infos.
        </div>
      </div>

      {loading && <div className="text-white/40 text-sm">Chargement…</div>}

      {!loading && items.length === 0 && (
        <div className="rounded-2xl border border-dashed border-white/15 bg-white/[0.02] p-10 text-center" data-testid="empty-state">
          <Users className="w-10 h-10 text-white/30 mx-auto mb-3" />
          <div className="text-white/70 text-sm font-semibold">Aucun moniteur enregistré</div>
          <div className="text-white/40 text-xs mt-1 max-w-md mx-auto">
            Ajoute ici les moniteurs, formateurs ou auto-écoles qui participent à ton apprentissage.
          </div>
        </div>
      )}

      {!loading && items.length > 0 && (
        <div className="grid sm:grid-cols-2 gap-3" data-testid="informal-list">
          {items.map((it) => (
            <div key={it.id} className="rounded-xl border border-white/10 bg-white/[0.02] p-4 flex flex-col gap-2 group">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="text-white font-semibold truncate">{it.teacher_name}</div>
                  {it.auto_ecole_name && (
                    <div className="text-white/60 text-[12px] mt-0.5 inline-flex items-center gap-1">
                      <Building2 size={11} /> {it.auto_ecole_name}
                    </div>
                  )}
                </div>
                <button
                  onClick={() => remove(it.id, it.teacher_name)}
                  className="opacity-0 group-hover:opacity-100 shrink-0 w-8 h-8 rounded-lg border border-red-500/20 bg-red-500/[0.04] text-red-400 hover:bg-red-500/10 transition inline-flex items-center justify-center"
                  data-testid={`btn-delete-${it.id}`}
                  aria-label="Supprimer"
                >
                  <Trash2 size={13} />
                </button>
              </div>
              <div className="flex flex-wrap gap-1.5 text-[11px] text-white/50">
                {it.teacher_email && <span className="inline-flex items-center gap-1"><Mail size={10} /> {it.teacher_email}</span>}
                {it.teacher_phone && <span className="inline-flex items-center gap-1">📞 {it.teacher_phone}</span>}
              </div>
              {it.notes && (
                <div className="text-[12px] text-white/60 mt-1 border-t border-white/[0.05] pt-2 line-clamp-2">
                  {it.notes}
                </div>
              )}
              {it.notify_email_requested && (
                <div className={`inline-flex items-center gap-1 text-[10.5px] font-semibold uppercase tracking-widest mt-1 ${it.notification_sent ? 'text-emerald-400' : 'text-amber-400'}`}>
                  {it.notification_sent
                    ? <><MailCheck size={11} /> Invitation envoyée</>
                    : <><Mail size={11} /> Invitation en attente</>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <NewInformalModal onClose={() => setShowModal(false)} onCreated={() => { setShowModal(false); load(); }} />
      )}
    </div>
  );
}

function NewInformalModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    teacher_name: '',
    teacher_email: '',
    teacher_phone: '',
    auto_ecole_name: '',
    notes: '',
    notify_email: false,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const canSubmit = form.teacher_name.trim().length >= 2 && !saving;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setError('');
    setSaving(true);
    try {
      await api.eleveMoniteursCreate({
        teacher_name: form.teacher_name.trim(),
        teacher_email: form.teacher_email.trim(),
        teacher_phone: form.teacher_phone.trim(),
        auto_ecole_name: form.auto_ecole_name.trim(),
        notes: form.notes.trim(),
        notify_email: form.notify_email,
      });
      toast.success('Moniteur ajouté', {
        description: form.notify_email && form.teacher_email ? 'Un email d\'invitation lui sera envoyé' : undefined,
        icon: <CheckCircle2 className="text-emerald-400" size={16} />,
      });
      onCreated();
    } catch (err) {
      setError(err?.message || 'Erreur inconnue');
    }
    setSaving(false);
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="new-informal-backdrop"
    >
      <div
        className="w-full max-w-md rounded-2xl border border-white/10 bg-[#111112] shadow-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        data-testid="new-informal-modal"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 sticky top-0 bg-[#111112] z-10">
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest text-rose-400">Nouveau · Trace perso</div>
            <h2 className="text-xl font-black text-white mt-0.5" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Ajouter un moniteur</h2>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg border border-white/10 bg-white/[0.03] text-white/60 hover:bg-white/10 hover:text-white transition inline-flex items-center justify-center"
            data-testid="btn-close-informal-modal"
            aria-label="Fermer"
          >
            <X size={16} />
          </button>
        </div>

        <form onSubmit={submit} className="p-5 space-y-4">
          <FieldInput
            label="Nom du moniteur"
            required
            value={form.teacher_name}
            onChange={(v) => setForm({ ...form, teacher_name: v })}
            placeholder="ex. Marc Dupont"
            testid="input-informal-name"
            autoFocus
          />
          <div className="grid grid-cols-2 gap-3">
            <FieldInput
              label="Email"
              type="email"
              value={form.teacher_email}
              onChange={(v) => setForm({ ...form, teacher_email: v })}
              placeholder="moniteur@…"
              testid="input-informal-email"
            />
            <FieldInput
              label="Téléphone"
              type="tel"
              value={form.teacher_phone}
              onChange={(v) => setForm({ ...form, teacher_phone: v })}
              placeholder="06…"
              testid="input-informal-phone"
            />
          </div>
          <FieldInput
            label="Auto-école"
            value={form.auto_ecole_name}
            onChange={(v) => setForm({ ...form, auto_ecole_name: v })}
            placeholder="ex. Auto-École du Centre"
            testid="input-informal-ecole"
          />
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wider text-white/50 mb-1.5">Notes personnelles</label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={2}
              maxLength={500}
              placeholder="Points forts, style d'enseignement…"
              className="w-full px-3 py-2 rounded-lg border border-white/10 bg-white/[0.03] text-white text-[13px] placeholder-white/25 focus:outline-none focus:border-rose-500/60 resize-none"
              data-testid="input-informal-notes"
            />
          </div>

          {/* Option invitation email */}
          <label className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition ${
            form.notify_email && form.teacher_email
              ? 'border-rose-500/40 bg-rose-500/[0.05]'
              : 'border-white/10 bg-white/[0.02] hover:bg-white/[0.04]'
          }`}>
            <input
              type="checkbox"
              checked={form.notify_email}
              onChange={(e) => setForm({ ...form, notify_email: e.target.checked })}
              disabled={!form.teacher_email}
              className="mt-0.5 accent-rose-500"
              data-testid="checkbox-notify-email"
            />
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-semibold text-white flex items-center gap-1.5">
                <Mail size={13} className="text-rose-400" /> Envoyer une invitation par email
              </div>
              <div className="text-[11.5px] text-white/50 mt-0.5">
                {form.teacher_email
                  ? 'Nous préviendrons ton moniteur qu\'il peut créer un compte code2roues gratuit pour te rattacher officiellement. Sans obligation.'
                  : 'Renseigne un email pour activer cette option.'}
              </div>
            </div>
          </label>

          {error && (
            <div className="rounded-xl border border-red-500/30 bg-red-500/[0.08] p-3 flex items-start gap-2 text-[12px] text-red-200">
              <AlertCircle className="shrink-0 mt-0.5" size={14} />
              <span>{error}</span>
            </div>
          )}

          <div className="pt-2 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 rounded-xl text-[13px] font-semibold text-white/60 hover:text-white hover:bg-white/[0.06] transition"
              data-testid="btn-cancel-informal"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-rose-600 hover:bg-rose-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-[13px] font-semibold shadow-md shadow-rose-900/30 transition"
              data-testid="btn-submit-informal"
            >
              {saving ? (<><Loader2 size={14} className="animate-spin" /> Enregistrement…</>) : (<><Plus size={14} /> Ajouter</>)}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function FieldInput({ label, required, value, onChange, type = 'text', placeholder, testid, autoFocus }) {
  return (
    <div>
      <label className="block text-[11px] font-semibold uppercase tracking-wider text-white/50 mb-1.5">
        {label} {required && <span className="text-rose-400">*</span>}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        autoFocus={autoFocus}
        maxLength={type === 'email' ? 200 : 100}
        data-testid={testid}
        className="w-full h-10 px-3 rounded-lg border border-white/10 bg-white/[0.03] text-white text-[13px] placeholder-white/25 focus:outline-none focus:border-rose-500/60 focus:bg-white/[0.05] transition"
      />
    </div>
  );
}
